import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { User } from "@supabase/supabase-js";
import { useCallback } from "react";

// Function to fetch the profile and organization data
const fetchUserProfile = async (userId: string | undefined) => {
  if (!userId) {
    // No user, no profile
    return null;
  }

  console.log(`[RQ] Fetching profile for user: ${userId}`);

  const { data, error, status } = await supabase
    .from("profiles")
    .select("*, organizations(*)") // Select profile and joined organization
    .eq("id", userId)
    .single();

  if (error && status !== 406) {
    // 406 status means no rows found, which is not necessarily an error here
    console.error("[RQ] Error fetching profile:", error);
    throw new Error(error.message);
  }

  // Handle case where organization might be null in the join
  if (data && !data.organizations && data.organization_id) {
    console.log(
      `[RQ] Profile fetched, but organization join failed. Fetching org separately for ID: ${data.organization_id}`,
    );
    const { data: orgData, error: orgError } = await supabase
      .from("organizations")
      .select("*")
      .eq("id", data.organization_id)
      .single();

    if (orgError && orgError.code !== "PGRST116") {
      console.error("[RQ] Error fetching organization separately:", orgError);
      // Don't throw, just return profile without org
    } else {
      // Manually attach the organization data
      data.organizations = orgData;
    }
  }

  console.log("[RQ] Profile fetch complete. Data:", data);
  return data;
};

// React Query hook
export function useFetchUserProfile(user: User | null) {
  const queryClient = useQueryClient();
  const userId = user?.id;

  // Check if this might be a new user scenario
  const isNewUserScenario =
    typeof window !== "undefined" &&
    sessionStorage.getItem("pendingNewUserRedirect") === "true";

  const {
    data: userProfileAndOrg,
    isLoading,
    error,
    refetch,
  } = useQuery({
    // Query key includes the user ID, so it refetches when the user changes
    queryKey: ["userProfile", userId],
    queryFn: () => fetchUserProfile(userId),
    enabled: !!userId, // Only run the query if there is a user ID
    // Reduce stale time for new users to ensure fresh data
    staleTime: isNewUserScenario ? 0 : 5 * 60 * 1000, // 0 for new users, 5 minutes for existing
    gcTime: 10 * 60 * 1000, // 10 minutes
    // Increase retry for new users who might be waiting for organization creation
    retry: isNewUserScenario ? 3 : 1,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 3000), // Exponential backoff, max 3s
    // Refetch more aggressively for new users
    refetchInterval: (data) => {
      // If we have a user profile but no organization, and it's a new user scenario, keep polling
      if (isNewUserScenario && data?.userProfile && !data?.organization) {
        console.log(
          "[RQ] New user missing organization, polling every 2 seconds",
        );
        return 2000; // Poll every 2 seconds
      }
      return false; // No polling for normal cases
    },
    select: (data) => {
      // Separate profile and organization from the fetched data
      if (!data) return { userProfile: null, organization: null };
      const { organizations, ...profile } = data;
      return {
        userProfile: profile,
        organization: organizations, // This will be the single org object or null
      };
    },
  });

  // Function to manually invalidate and refetch
  const refreshProfile = useCallback(async () => {
    console.log("[RQ] Manually refreshing profile...");
    // Invalidate the query to force a fresh fetch
    queryClient.invalidateQueries({ queryKey: ["userProfile", userId] });
    // Also call refetch to immediately trigger the refetch
    const result = await refetch();
    console.log("[RQ] Profile refresh complete");
    return result;
  }, [queryClient, userId, refetch]);

  return {
    userProfile: userProfileAndOrg?.userProfile ?? null,
    organization: userProfileAndOrg?.organization ?? null,
    isLoading,
    error,
    refreshProfile, // Use our custom refresh function
  };
}
